import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, FileText, Wallet, UserPlus, X, Check, AlertTriangle, Mail, MailX, MailQuestion, Download, Send, ClipboardCheck, ShieldCheck,
} from 'lucide-react';
import { SifaMitarbeiter, SifaTeilnahme, SifaUnterweisung } from '../../services/sifa/types';
import { findeMitarbeiter } from '../../services/sifa/mitarbeiterMatch';
import { fetchTeilnahmeDetail } from '../../services/sifa/sifaService';
import { ZertifikatBuero, erzeugeZertifikatImBrowser, sendeZertifikatErneut } from '../../services/sifa/zertifikatClient';
import { describeError } from '../../services/errorMessage';
import {
  FILTER_LABEL,
  TeilnahmenFilter,
  ergebnisText,
  filtereTeilnahmen,
  formatDatum,
  formatZeit,
  istAbrechenbar,
  teilnehmerName,
  versandStatus,
  zaehleTeilnahmen,
} from './unterweisungModulLogic';
import { bestaetigungAnzeige } from './unterweisungAblageLogic';

interface Props {
  teilnahmen: SifaTeilnahme[];
  unterweisungen: SifaUnterweisung[];
  mitarbeiter: SifaMitarbeiter[];
  /** Nur die Teilnahmen dieses Kurses; null = alle Kurse des Projekts. */
  unterweisungId?: string | null;
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Teilnahme, deren Zertifikat gerade geoeffnet wird. */
  zertifikatBusyId?: string | null;
  /** Laeuft gerade eine Buchung? Sperrt die Auswahl. */
  buchungLaeuft?: boolean;
  /** Ob die Abrechnung angeboten wird (LV-Position vorhanden, nicht Nur-Ansicht, onUpdate da). */
  kannAbrechnen: boolean;
  /** Buero auf dem Blatt (Profil des Nutzers) — fuer Download und erneuten Versand im Browser. */
  buero: ZertifikatBuero;
  /** Kunde = Projekt, wie er auf dem Blatt steht. */
  kundeName: string;
  /** Liste nach einem erneuten Versand neu laden (Versandstand kommt aus der Datenbank). */
  onNeuLaden: () => Promise<void> | void;
  onZertifikat: (t: SifaTeilnahme) => void;
  onZuordnen: (t: SifaTeilnahme, mitarbeiterId: string) => Promise<void>;
  onAbrechnen: (ids: string[]) => void;
}

/**
 * Die Teilnahmen — je Kurs oder ueber das ganze Projekt.
 *
 * Eine Zeile je Durchfuehrung mit den EINGEFRORENEN Angaben der Person (so
 * steht es auf dem Zertifikat), Ergebnis, Zertifikat, Versandstand,
 * Mitarbeiter-Zuordnung und Abrechnung. Die Zuordnung ist der Punkt, an dem
 * der Innendienst eingreift: die automatische Regel „3 von 4" laesst eine
 * Teilnahme bei Gleichstand bewusst ohne Person (mitarbeiterMatch.ts) —
 * hier wird sie von Hand zugeordnet, mit den Kandidaten der Regel als
 * Vorschlag.
 *
 * Die Abrechnung waehlt mehrere Teilnahmen (nur bestandene, nicht gebuchte)
 * und reicht die ids an den Container; der haelt den Buchungsriegel.
 */
export const TeilnahmenListe: React.FC<Props> = ({
  teilnahmen,
  unterweisungen,
  mitarbeiter,
  unterweisungId = null,
  isReadOnly = false,
  isMobile = false,
  zertifikatBusyId = null,
  buchungLaeuft = false,
  kannAbrechnen,
  buero,
  kundeName,
  onNeuLaden,
  onZertifikat,
  onZuordnen,
  onAbrechnen,
}) => {
  const [filter, setFilter] = useState<TeilnahmenFilter>('alle');
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [zuordnenFuer, setZuordnenFuer] = useState<SifaTeilnahme | null>(null);
  const [detailFuer, setDetailFuer] = useState<SifaTeilnahme | null>(null);
  const [downloadBusyId, setDownloadBusyId] = useState<string | null>(null);
  const [sendenBusyId, setSendenBusyId] = useState<string | null>(null);
  /** Rueckmeldung zu GENAU einer Zeile (Download/Versand) — steht unter deren Versandstand. */
  const [zeilenMeldung, setZeilenMeldung] = useState<{ id: string; ton: 'info' | 'fehler'; text: string } | null>(null);

  const basis = useMemo(
    () => (unterweisungId ? teilnahmen.filter((t) => t.unterweisungId === unterweisungId) : teilnahmen),
    [teilnahmen, unterweisungId],
  );
  const sichtbar = useMemo(() => filtereTeilnahmen(basis, filter), [basis, filter]);
  const zaehler = zaehleTeilnahmen(basis);
  const titelVon = (id: string) => {
    const u = unterweisungen.find((x) => x.id === id);
    return u ? `Nr. ${u.nummer} ${u.titel}` : '—';
  };
  const mitarbeiterVon = (id?: string | null) => (id ? mitarbeiter.find((m) => m.id === id) || null : null);
  /** Reiner Kurstitel fuer das Blatt — ohne das „Nr. x" der Listenanzeige. */
  const kursTitelVon = (id: string) => unterweisungen.find((x) => x.id === id)?.titel || 'Unterweisung';

  // ─── Zertifikat im Browser: herunterladen / erneut senden ────────────────

  const herunterladen = async (t: SifaTeilnahme) => {
    setDownloadBusyId(t.id);
    setZeilenMeldung(null);
    try {
      const { bytes, dateiname } = await erzeugeZertifikatImBrowser(t, kursTitelVon(t.unterweisungId), buero, kundeName);
      const { saveAs } = await import('file-saver');
      saveAs(new Blob([bytes], { type: 'application/pdf' }), dateiname);
    } catch (e) {
      setZeilenMeldung({ id: t.id, ton: 'fehler', text: `Download fehlgeschlagen: ${describeError(e)}` });
    } finally {
      setDownloadBusyId(null);
    }
  };

  const erneutSenden = async (t: SifaTeilnahme) => {
    const empfaenger = (t.email || '').trim();
    if (!empfaenger) {
      setZeilenMeldung({ id: t.id, ton: 'fehler', text: 'Die Teilnahme trägt keine E-Mail-Adresse.' });
      return;
    }
    const schonGesendet = !!t.zertifikatGesendetAm;
    const frage = schonGesendet
      ? `Das Zertifikat ${t.zertifikatNr || ''} wurde bereits gesendet (${formatZeit(t.zertifikatGesendetAm)}). Erneut an ${empfaenger} senden?`
      : `Zertifikat ${t.zertifikatNr || ''} jetzt an ${empfaenger} senden?`;
    if (!window.confirm(frage)) return;
    setSendenBusyId(t.id);
    setZeilenMeldung(null);
    try {
      await sendeZertifikatErneut(t, kursTitelVon(t.unterweisungId), buero, kundeName);
      setZeilenMeldung({ id: t.id, ton: 'info', text: `Gesendet an ${empfaenger}.` });
      await onNeuLaden();
    } catch (e) {
      setZeilenMeldung({ id: t.id, ton: 'fehler', text: `Versand fehlgeschlagen: ${describeError(e)}` });
    } finally {
      setSendenBusyId(null);
    }
  };

  const abrechenbarSichtbar = sichtbar.filter(istAbrechenbar);
  const gewaehlt = abrechenbarSichtbar.filter((t) => auswahl.has(t.id));
  const alleGewaehlt = abrechenbarSichtbar.length > 0 && gewaehlt.length === abrechenbarSichtbar.length;

  const umschalten = (id: string) => setAuswahl((prev) => {
    const neu = new Set(prev);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    return neu;
  });
  const alleUmschalten = () => setAuswahl(alleGewaehlt ? new Set() : new Set(abrechenbarSichtbar.map((t) => t.id)));

  /**
   * Versandstand als TEXT, nicht nur als Symbol: ein Fehler muss lesbar sein,
   * sonst weiss niemand, warum keine Mail kam. Darunter die beiden Wege des
   * Innendienstes, die nicht am Server-Endpunkt haengen.
   */
  const versandZelle = (t: SifaTeilnahme) => {
    const s = versandStatus(t);
    if (s === 'keins') return <span className="text-slate-300">—</span>;
    const Icon = s === 'gesendet' ? Mail : s === 'fehler' ? MailX : MailQuestion;
    const ton = s === 'gesendet' ? 'text-emerald-700' : s === 'fehler' ? 'text-red-700' : 'text-amber-700';
    const busy = downloadBusyId === t.id || sendenBusyId === t.id;
    const meldung = zeilenMeldung && zeilenMeldung.id === t.id ? zeilenMeldung : null;
    return (
      <div className="space-y-1.5 min-w-[200px] max-w-[320px]">
        <p className={`flex items-start gap-1 text-[11px] font-semibold ${ton}`}>
          <Icon className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span className="break-words whitespace-normal">
            {s === 'gesendet' && `Gesendet ${formatZeit(t.zertifikatGesendetAm)}`}
            {s === 'fehler' && `Versand fehlgeschlagen: ${t.zertifikatFehler}`}
            {s === 'offen' && 'Versand steht aus'}
          </span>
        </p>
        {s === 'gesendet' && t.zertifikatFehler && (
          <p className="text-[10px] text-slate-500 break-words whitespace-normal">{t.zertifikatFehler}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => void herunterladen(t)}
            disabled={busy}
            title="Zertifikat als PDF im Browser erzeugen und speichern"
            className="min-h-[32px] px-2 flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-40"
          >
            {downloadBusyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Zertifikat herunterladen
          </button>
          {!isReadOnly && (
            <button
              onClick={() => void erneutSenden(t)}
              disabled={busy || !(t.email || '').trim()}
              title={(t.email || '').trim() ? `An ${t.email} senden` : 'Keine E-Mail-Adresse an der Teilnahme'}
              className="min-h-[32px] px-2 flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg disabled:opacity-40"
            >
              {sendenBusyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {s === 'gesendet' ? 'Erneut senden' : 'Senden'}
            </button>
          )}
        </div>
        {meldung && (
          <p className={`text-[11px] break-words whitespace-normal ${meldung.ton === 'fehler' ? 'text-red-700' : 'text-emerald-700'}`} role={meldung.ton === 'fehler' ? 'alert' : undefined}>
            {meldung.text}
          </p>
        )}
      </div>
    );
  };

  const zuordnungZelle = (t: SifaTeilnahme) => {
    const m = mitarbeiterVon(t.mitarbeiterId);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {m ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><Check className="w-3.5 h-3.5" /> {m.vorname} {m.nachname}</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> nicht zugeordnet</span>
        )}
        {!isReadOnly && (
          <button onClick={() => setZuordnenFuer(t)} className="text-[11px] font-bold text-blue-700 hover:underline flex items-center gap-0.5">
            <UserPlus className="w-3 h-3" /> {m ? 'Ändern' : 'Zuordnen'}
          </button>
        )}
      </div>
    );
  };

  const abrechnungZelle = (t: SifaTeilnahme) => (
    t.gebuchtAm ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600" title={`Gebucht am ${formatDatum(t.gebuchtAm)}`}><Wallet className="w-3.5 h-3.5" /> gebucht</span>
    ) : istAbrechenbar(t) ? (
      <span className="text-[11px] font-semibold text-amber-700">offen</span>
    ) : (
      <span className="text-slate-300">—</span>
    )
  );

  /** Unterschrift und Bestaetigung stehen nicht in der Liste (Spalten der Listenabfrage) — erst im Detail. */
  const detailKnopf = (t: SifaTeilnahme) => (
    <button onClick={() => setDetailFuer(t)} className="mt-0.5 min-h-[28px] inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:underline">
      <ClipboardCheck className="w-3.5 h-3.5" /> Details
    </button>
  );

  const zertifikatKnopf = (t: SifaTeilnahme) => (
    t.zertifikatPfad ? (
      <button onClick={() => onZertifikat(t)} disabled={zertifikatBusyId === t.id} className="min-h-[36px] px-2.5 flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg disabled:opacity-40">
        {zertifikatBusyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
      </button>
    ) : <span className="text-slate-300">—</span>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(FILTER_LABEL) as TeilnahmenFilter[]).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filter === f ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {FILTER_LABEL[f]}
            {f === 'unabgerechnet' && zaehler.unabgerechnet > 0 ? ` (${zaehler.unabgerechnet})` : ''}
            {f === 'ohne_mitarbeiter' && zaehler.ohneMitarbeiter > 0 ? ` (${zaehler.ohneMitarbeiter})` : ''}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-auto">{sichtbar.length} von {basis.length}</span>
      </div>

      {kannAbrechnen && abrechenbarSichtbar.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
          <label className="flex items-center gap-2 text-xs font-semibold text-emerald-900">
            <input type="checkbox" checked={alleGewaehlt} onChange={alleUmschalten} disabled={buchungLaeuft} className="w-4 h-4 accent-emerald-600" />
            Alle abrechenbaren wählen ({abrechenbarSichtbar.length})
          </label>
          <button
            onClick={() => onAbrechnen(gewaehlt.map((t) => t.id))}
            disabled={gewaehlt.length === 0 || buchungLaeuft}
            className="ml-auto min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-40"
          >
            {buchungLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            {gewaehlt.length === 1 ? '1 Teilnahme abrechnen' : `${gewaehlt.length} Teilnahmen abrechnen`}
          </button>
        </div>
      )}

      {sichtbar.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center text-xs text-slate-500">
          {basis.length === 0 ? 'Noch keine Teilnahme. Sobald jemand den Kundenlink öffnet, erscheint sie hier.' : 'Kein Eintrag für diesen Filter.'}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{teilnehmerName(t)}</p>
                  <p className="text-xs text-slate-500">{formatZeit(t.abgeschlossenAm || t.begonnenAm)}{!unterweisungId ? ` · ${titelVon(t.unterweisungId)}` : ''}</p>
                  <p className="text-xs text-slate-600">{ergebnisText(t)}{t.zertifikatNr ? ` · ${t.zertifikatNr}` : ''}</p>
                  {detailKnopf(t)}
                </div>
                {kannAbrechnen && istAbrechenbar(t) && (
                  <input type="checkbox" checked={auswahl.has(t.id)} onChange={() => umschalten(t.id)} disabled={buchungLaeuft} className="w-5 h-5 accent-emerald-600 mt-1" />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">{abrechnungZelle(t)}{zertifikatKnopf(t)}</div>
              {versandZelle(t)}
              {zuordnungZelle(t)}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                {kannAbrechnen && <th className="px-3 py-2 w-8"></th>}
                <th className="px-3 py-2 text-left font-bold">Datum</th>
                {!unterweisungId && <th className="px-3 py-2 text-left font-bold">Kurs</th>}
                <th className="px-3 py-2 text-left font-bold">Name</th>
                <th className="px-3 py-2 text-left font-bold">Geburtsdatum</th>
                <th className="px-3 py-2 text-left font-bold">E-Mail</th>
                <th className="px-3 py-2 text-left font-bold">Ergebnis</th>
                <th className="px-3 py-2 text-left font-bold">Zertifikat</th>
                <th className="px-3 py-2 text-left font-bold">Versand</th>
                <th className="px-3 py-2 text-left font-bold">Mitarbeiter</th>
                <th className="px-3 py-2 text-left font-bold">Abrechnung</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60 align-top">
                  {kannAbrechnen && (
                    <td className="px-3 py-2">
                      {istAbrechenbar(t) && <input type="checkbox" checked={auswahl.has(t.id)} onChange={() => umschalten(t.id)} disabled={buchungLaeuft} className="w-4 h-4 accent-emerald-600" />}
                    </td>
                  )}
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatZeit(t.abgeschlossenAm || t.begonnenAm)}</td>
                  {!unterweisungId && <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={titelVon(t.unterweisungId)}>{titelVon(t.unterweisungId)}</td>}
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{teilnehmerName(t)}</p>
                    {detailKnopf(t)}
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{t.geburtsdatum ? formatDatum(t.geburtsdatum) : <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600 break-all">{t.email || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{ergebnisText(t)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {t.zertifikatNr && <span className="text-[11px] font-mono text-slate-600 whitespace-nowrap">{t.zertifikatNr}</span>}
                      {zertifikatKnopf(t)}
                    </div>
                    {t.gueltigBis && <p className="text-[10px] text-slate-400 mt-0.5">gültig bis {formatDatum(t.gueltigBis)}</p>}
                  </td>
                  <td className="px-3 py-2">{versandZelle(t)}</td>
                  <td className="px-3 py-2">{zuordnungZelle(t)}</td>
                  <td className="px-3 py-2">{abrechnungZelle(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailFuer && (
        <TeilnahmeDetailDialog
          teilnahme={detailFuer}
          kursTitel={titelVon(detailFuer.unterweisungId)}
          onClose={() => setDetailFuer(null)}
        />
      )}

      {zuordnenFuer && (
        <ZuordnenDialog
          teilnahme={zuordnenFuer}
          mitarbeiter={mitarbeiter}
          onClose={() => setZuordnenFuer(null)}
          onWaehlen={async (id) => { await onZuordnen(zuordnenFuer, id); setZuordnenFuer(null); }}
        />
      )}
    </div>
  );
};

/**
 * Teilnahme einer Person zuordnen. Oben die Kandidaten der Regel „3 von 4"
 * (findeMitarbeiter — dieselbe TS-Vorschau wie im Mitarbeiterdialog), darunter
 * die Suche ueber den ganzen Stamm fuer den Fall, dass die Angaben der
 * Teilnahme zu weit abweichen (Tippfehler, Zweitname).
 */
const ZuordnenDialog: React.FC<{
  teilnahme: SifaTeilnahme;
  mitarbeiter: SifaMitarbeiter[];
  onClose: () => void;
  onWaehlen: (mitarbeiterId: string) => Promise<void>;
}> = ({ teilnahme, mitarbeiter, onClose, onWaehlen }) => {
  const [suche, setSuche] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const ergebnis = useMemo(
    () => findeMitarbeiter({ vorname: teilnahme.vorname, nachname: teilnahme.nachname, geburtsdatum: teilnahme.geburtsdatum, email: teilnahme.email }, mitarbeiter),
    [teilnahme, mitarbeiter],
  );
  const kandidatenIds = new Set(ergebnis.kandidaten.map((m) => m.id));
  const s = suche.trim().toLowerCase();
  const treffer = mitarbeiter
    .filter((m) => !kandidatenIds.has(m.id))
    .filter((m) => !s || `${m.vorname} ${m.nachname} ${m.email || ''} ${m.abteilung || ''}`.toLowerCase().includes(s))
    .slice(0, 30);

  const waehlen = async (id: string) => {
    setBusyId(id);
    setFehler(null);
    try { await onWaehlen(id); } catch (e: any) { setFehler(e?.message || String(e)); } finally { setBusyId(null); }
  };

  const zeile = (m: SifaMitarbeiter, hervor: boolean) => (
    <button key={m.id} onClick={() => void waehlen(m.id)} disabled={busyId !== null} className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between gap-2 hover:bg-slate-50 disabled:opacity-50 ${hervor ? 'border-teal-300 bg-teal-50/40' : 'border-slate-200'}`}>
      <span className="min-w-0">
        <span className="text-sm font-semibold text-slate-800">{m.vorname} {m.nachname}</span>
        <span className="block text-[11px] text-slate-500 truncate">{[m.geburtsdatum ? formatDatum(m.geburtsdatum) : null, m.email, m.abteilung].filter(Boolean).join(' · ') || '—'}</span>
      </span>
      {busyId === m.id ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Check className="w-4 h-4 text-teal-600 shrink-0" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">Mitarbeiter zuordnen</h3>
            <p className="text-xs text-slate-500 truncate">
              {teilnehmerName(teilnahme)}{teilnahme.geburtsdatum ? `, ${formatDatum(teilnahme.geburtsdatum)}` : ''}{teilnahme.email ? `, ${teilnahme.email}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          {fehler && <p className="text-xs text-red-700">{fehler}</p>}
          {ergebnis.kandidaten.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase text-teal-700">Vorschläge (mindestens 3 von 4 Angaben gleich)</p>
              {ergebnis.kandidaten.map((m) => zeile(m, true))}
            </div>
          )}
          <div className="space-y-1.5">
            <p className="text-[11px] font-bold uppercase text-slate-500">Alle Mitarbeiter</p>
            <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Suchen…" className="w-full px-3 py-2 text-sm bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-teal-300" />
            {treffer.length === 0 ? (
              <p className="text-xs text-slate-500">Keine weitere Person im Stamm{s ? ' für diese Suche' : ''}. Neue Personen legt das Mitarbeitermodul an.</p>
            ) : treffer.map((m) => zeile(m, false))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Eine Teilnahme im Detail: Unterschrift und Abschluss-Bestaetigung (Nr. 143)
 * samt Fassung der Datenschutzhinweise. Beides liest erst
 * fetchTeilnahmeDetail — die Listenabfrage laesst die Spalten bewusst weg
 * (Bildgroesse; ohne Nr. 143 liefe sie sonst auf 42703). Bis das Detail da
 * ist, steht keine Aussage zur Bestaetigung da: aus der Listenzeile saehe
 * jede Teilnahme wie „vor Einfuehrung" aus.
 */
const TeilnahmeDetailDialog: React.FC<{
  teilnahme: SifaTeilnahme;
  kursTitel: string;
  onClose: () => void;
}> = ({ teilnahme, kursTitel, onClose }) => {
  /** undefined = laedt noch; null = nicht (mehr) gefunden. */
  const [detail, setDetail] = useState<SifaTeilnahme | null | undefined>(undefined);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    fetchTeilnahmeDetail(teilnahme.id)
      .then((d) => { if (aktiv) setDetail(d); })
      .catch((e) => { if (aktiv) { setFehler(describeError(e)); setDetail(null); } });
    return () => { aktiv = false; };
  }, [teilnahme.id]);

  const t = detail ?? teilnahme;
  const b = detail ? bestaetigungAnzeige(detail) : null;
  const zeile = (label: string, wert: React.ReactNode) => (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-xs">
      <span className="font-bold text-slate-500">{label}</span>
      <span className="text-slate-800 break-words">{wert}</span>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Teilnahme — ${teilnehmerName(t)}`}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800">Teilnahme — {teilnehmerName(t)}</h3>
            <p className="text-xs text-slate-500 truncate">{kursTitel}</p>
          </div>
          {/* Fokus hierher: Escape wirkt sofort, Enter schliesst nur. */}
          <button type="button" autoFocus onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="space-y-1.5">
            {zeile('Begonnen', formatZeit(t.begonnenAm))}
            {zeile('Abgeschlossen', t.abgeschlossenAm ? formatZeit(t.abgeschlossenAm) : '—')}
            {zeile('Ergebnis', ergebnisText(t))}
            {zeile('Geburtsdatum', t.geburtsdatum ? formatDatum(t.geburtsdatum) : '—')}
            {zeile('E-Mail', t.email || '—')}
            {zeile('Zertifikat', t.zertifikatNr ? `${t.zertifikatNr}${t.gueltigBis ? ` · gültig bis ${formatDatum(t.gueltigBis)}` : ''}` : '—')}
          </div>

          <div className="space-y-2">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Bestätigung
            </p>
            {detail === undefined ? (
              <div className="py-3 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
            ) : fehler ? (
              <p className="text-xs text-red-700">Das Detail ließ sich nicht laden: {fehler}</p>
            ) : !b ? (
              <p className="text-xs text-slate-500">Diese Teilnahme wurde nicht gefunden.</p>
            ) : b.art === 'bestaetigt' ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1.5">
                <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> {b.amText}</p>
                {b.wortlaut && <p className="text-xs text-slate-700 whitespace-pre-line">„{b.wortlaut}"</p>}
                <p className="text-[11px] text-slate-600">{b.datenschutzText}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-500">{b.text}</p>
            )}
          </div>

          {detail?.unterschrift && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">Unterschrift</p>
              <img src={detail.unterschrift} alt="Unterschrift" className="max-w-full max-h-32 bg-white border border-slate-200 rounded-lg" />
              <p className="text-[11px] text-slate-500">
                {detail.unterschriftAm ? `am ${formatZeit(detail.unterschriftAm)}` : ''}
                {detail.unterschriftWeg ? ` · ${detail.unterschriftWeg === 'qr' ? 'per QR am Handy' : 'direkt'}` : ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
